import React, { useState } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import { Plus, Minus } from "lucide-react";
import { COMPANY } from "../content";

const SERVICES = [
  {
    name: "Civil works",
    desc: "Structural alterations, partition walls, demolition, plastering and waterproofing — delivered by our own civil team across Abu Dhabi, Khalifa City and the islands.",
  },
  {
    name: "MEP (Electrical, Plumbing, HVAC)",
    desc: "Re-routing electrical circuits, plumbing layouts and HVAC zoning for villas on Saadiyat, Yas Island and Al Reem — coordinated as a single workflow.",
  },
  {
    name: "Joinery & custom furniture",
    desc: "Wardrobes, kitchens, vanities, beds and wall panels — designed at our Abu Dhabi studio, manufactured in our UAE workshop, installed by our own joiners.",
  },
  {
    name: "Full interior finishing",
    desc: "Flooring, ceilings, painting, lighting, curtains, upholstery and styling — every surface finished to handover standard, ready for the day you move in.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Site visit & brief",
    body: "We meet you at your Abu Dhabi address — Saadiyat, Yas, Al Reem, Khalifa City or Reem Hills — measure the space, and listen to how it needs to work for you.",
  },
  {
    n: "02",
    title: "Design & costing",
    body: "Concept layouts, finish boards and a transparent itemised quote — so you know exactly what you're paying for before any work begins on site.",
  },
  {
    n: "03",
    title: "Approvals & mobilisation",
    body: "We handle the building, community and authority approvals required in Abu Dhabi — drawings, NOCs, deposits and access scheduling — so you don't have to chase paperwork.",
  },
  {
    n: "04",
    title: "Execution under one roof",
    body: "Civil, MEP, joinery and finishing happen in coordinated phases under one accountable project lead — not a chain of subcontractors who blame each other when issues come up.",
  },
  {
    n: "05",
    title: "Snag, handover, support",
    body: "Final snag list, deep clean, walkthrough, and a defects warranty period — we stay accountable after handover, not just before the cheque clears.",
  },
];

const FAQS = [
  {
    q: "What is a turnkey fit-out in Abu Dhabi?",
    a: "Turnkey fit-out means one contractor takes responsibility for the entire interior — design, civil works, MEP, joinery, furniture and finishing — and hands over a fully completed, ready-to-use space. In Abu Dhabi this matters even more, because building approvals, community NOCs and trade scheduling can be slow with multiple subcontractors. One contract, one team, one finished home or office.",
  },
  {
    q: "Do you handle Abu Dhabi approvals and NOCs?",
    a: "Yes. Our team manages building, community and authority approvals across Abu Dhabi — including consultant drawings, Aldar / Modon / TAMM permits, security deposits and access scheduling for villa, apartment and office projects.",
  },
  {
    q: "Which Abu Dhabi areas do you serve?",
    a: "We deliver fit-out projects across Saadiyat Island, Yas Island, Al Reem Island, Reem Hills, Khalifa City, Al Raha Beach, Al Reef, the city corniche, and into Al Ain. Our workshop is UAE-based, which means same-week site visits anywhere in the emirate.",
  },
  {
    q: "Do you provide full execution from start to finish?",
    a: "Yes. From the first demolition strip-out to the final styled handover photo, every phase is delivered by our own teams — civil, MEP, joinery, furniture, upholstery, curtains and finishing. We do not subcontract the work that defines our standard.",
  },
];

const WHATSAPP = `https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappFitOutText}`;

export default function InteriorFitOutAbuDhabi() {
  const [openFaq, setOpenFaq] = useState(0);

  usePageMeta({
    title:
      "Interior Fit-Out Company in Abu Dhabi | Turnkey Contractor — Time Furniture",
    description:
      "Turnkey interior fit-out contractor in Abu Dhabi. Civil works, MEP, joinery, custom furniture and full finishing — delivered in-house since 1993, across Saadiyat, Yas, Al Reem and beyond.",
    canonical: "https://timefurniture.ae/interior-fit-out-abu-dhabi",
    ogImage: "/media/team-projects/time-furniture-finishing-installation-team.webp",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        url: "https://timefurniture.ae/interior-fit-out-abu-dhabi",
        name: "Interior Fit-Out Company in Abu Dhabi — Time Furniture",
        description:
          "Turnkey interior fit-out contractor in Abu Dhabi — civil works, MEP, joinery, custom furniture and full finishing across Saadiyat, Yas, Al Reem, Khalifa City and Al Ain.",
        mainEntity: {
          "@type": "Service",
          name: "Turnkey Interior Fit-Out — Abu Dhabi",
          provider: { "@id": "https://timefurniture.ae/#organization" },
          areaServed: [
            "Abu Dhabi",
            "Saadiyat Island",
            "Yas Island",
            "Al Reem Island",
            "Khalifa City",
            "Al Ain",
            "United Arab Emirates",
          ],
          serviceType: [
            "Civil works",
            "MEP (Electrical, Plumbing, HVAC)",
            "Joinery and custom furniture",
            "Full interior finishing",
          ],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  return (
    <div data-testid="ifo-abudhabi-page" className="bg-[#FAF5EC]">
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Interior Fit-Out Abu Dhabi" },
        ]}
      />

      <section className="max-w-[1100px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-16 md:pb-20">
        <p className="eyebrow mb-5">Interior Fit-Out · Abu Dhabi</p>
        <h1
          data-testid="ifo-h1"
          className="font-serif-display text-4xl md:text-6xl lg:text-[68px] leading-[1.05] tracking-tight text-[#1C110C]"
        >
          Interior Fit-Out Company in{" "}
          <em className="text-[#6E1F2C] not-italic">Abu Dhabi</em>.
        </h1>
      </section>

      <section
        data-testid="ifo-intro"
        className="max-w-[1100px] mx-auto px-6 md:px-12 pb-20 md:pb-28"
      >
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-5">
          Introduction
        </h2>
        <p className="text-[#3E2A22] leading-[1.85] text-base md:text-[17px] max-w-3xl">
          We are a turnkey interior fit-out contractor in Abu Dhabi providing
          complete project execution from concept to completion. Since 1993,
          we&rsquo;ve handed over villas, apartments, offices and clinics
          across Saadiyat Island, Yas Island, Al Reem, Khalifa City and into
          Al Ain — with civil works, MEP, joinery and finishing all delivered
          by our own team.
        </p>
      </section>

      <section
        data-testid="ifo-services"
        className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-10">
            Services we deliver in-house
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {SERVICES.map((s, i) => (
              <div
                key={s.name}
                data-testid={`ifo-service-${i}`}
                className="border-t border-[#6E1F2C]/20 pt-6"
              >
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963] mb-3">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="font-serif-display text-xl md:text-2xl text-[#1C110C] mb-3">
                  {s.name}
                </h3>
                <p className="text-[#3E2A22] leading-[1.8] text-base">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="ifo-process"
        className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28"
      >
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-10">
          How we work — step by step
        </h2>
        <ol className="space-y-10">
          {STEPS.map((s) => (
            <li
              key={s.n}
              data-testid={`ifo-step-${s.n}`}
              className="grid grid-cols-12 gap-6 md:gap-10 border-t border-[#6E1F2C]/15 pt-8"
            >
              <p className="col-span-2 md:col-span-1 font-serif-display text-2xl md:text-3xl text-[#D4A963]">
                {s.n}
              </p>
              <div className="col-span-10 md:col-span-11">
                <h3 className="font-serif-display text-xl md:text-2xl text-[#1C110C] mb-3">
                  {s.title}
                </h3>
                <p className="text-[#3E2A22] leading-[1.85] text-base md:text-[17px] max-w-2xl">
                  {s.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section
        data-testid="ifo-why"
        className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-10">
            Why Abu Dhabi clients choose Time Furniture
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                t: "In-house execution",
                d: "Civil, MEP, joinery, upholstery and finishing — all under one roof, no subcontracting handoffs that slow down Abu Dhabi sites.",
              },
              {
                t: "30+ years of experience",
                d: "Established in 1993. Three decades of UAE projects mean fewer surprises and faster decisions on Saadiyat, Yas and Al Reem builds.",
              },
              {
                t: "Coverage across the emirate",
                d: "Saadiyat, Yas, Al Reem, Khalifa City, Al Ain — same accountable team and standard regardless of location.",
              },
            ].map((w) => (
              <div key={w.t} className="border-t border-[#6E1F2C]/20 pt-6">
                <h3 className="font-serif-display text-xl md:text-2xl text-[#1C110C] mb-3">
                  {w.t}
                </h3>
                <p className="text-[#3E2A22] leading-[1.8] text-base">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="ifo-faq"
        className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28"
      >
        <p className="eyebrow mb-5">Common Questions</p>
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-10">
          Frequently asked
        </h2>
        <div className="border-t border-[#6E1F2C]/20">
          {FAQS.map((f, i) => {
            const isOpen = openFaq === i;
            return (
              <div
                key={f.q}
                data-testid={`ifo-faq-item-${i}`}
                className="border-b border-[#6E1F2C]/20"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  data-testid={`ifo-faq-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-6 py-7 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="font-serif-display text-lg md:text-2xl text-[#1C110C] leading-snug">
                    {f.q}
                  </span>
                  <span className="text-[#6E1F2C] flex-shrink-0">
                    {isOpen ? <Minus size={20} /> : <Plus size={20} />}
                  </span>
                </button>
                {isOpen && (
                  <p className="pb-8 pr-12 text-[#3E2A22] leading-[1.85] text-base md:text-[17px]">
                    {f.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section data-testid="ifo-cta" className="bg-[#1C110C] text-[#FAF5EC]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.08] text-[#FAF5EC] max-w-3xl mx-auto">
            Planning a fit-out in{" "}
            <em className="text-[#D4A963] not-italic">Abu Dhabi</em>?
          </h2>
          <p className="mt-7 max-w-xl mx-auto text-[#EDE1C8]/85 leading-[1.7] text-base md:text-lg">
            Book a free consultation. We&rsquo;ll review your scope and reply
            with an indicative timeline and budget — no pressure, no obligation.
          </p>
          <a
            href={WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ifo-cta-consultation"
            className="mt-10 inline-flex items-center justify-center gap-3 bg-[#D4A963] text-[#1C110C] px-10 py-5 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#c9a166] transition-all"
          >
            Get Free Consultation →
          </a>
          <p className="mt-8 text-xs tracking-[0.04em] text-[#EDE1C8]/60">
            Crafted since 1993 · Saadiyat · Yas · Al Reem · Khalifa City · Al Ain
          </p>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-12 py-12 text-center">
        <p className="text-sm text-[#7A6B5F]">
          Also serving{" "}
          <Link
            to="/interior-fit-out-dubai"
            className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]"
          >
            Dubai
          </Link>{" "}
          and{" "}
          <Link
            to="/interior-fit-out-sharjah"
            className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]"
          >
            Sharjah
          </Link>
          .
        </p>
      </section>
    </div>
  );
}
